import { useMemo, useState } from 'react';
import {
    Alert, Box, Button, Chip, FormControl, FormHelperText, FormLabel,
    Input, LinearProgress, Option, Select, Sheet, Stack, Table, Typography,
} from '@mui/joy';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';

import { useAdvertiser } from '../../../AdvertiserContext';
import { APIAdsForecast, adsErrorText } from '../../../api_ads';
import AdPlacementsPicker from '../../../components/Ads/AdPlacementsPicker';
import AdTargetingEditor from '../../../components/Ads/AdTargetingEditor';
import { useAdsAccount, useAdsPlacements } from '../../../components/Ads/useAdsData';
import { PRICING_LABELS, formatInt, formatMoney } from '../../../components/Ads/adsConstants';


const CONFIDENCE = {
    high: { label: 'Высокая достоверность', color: 'success',
            hint: 'Накоплено достаточно данных по этим площадкам' },
    medium: { label: 'Средняя достоверность', color: 'primary',
              hint: 'Данных хватает для оценки порядка величины' },
    low: { label: 'Низкая достоверность', color: 'warning',
           hint: 'Данных по площадкам пока мало — цифры ориентировочные' },
};


export default function AdsForecastPage() {
    const { advertiserToken: token } = useAdvertiser();
    const { limits } = useAdsAccount();
    const { placements } = useAdsPlacements();

    const [pricingModel, setPricingModel] = useState('cpc');
    const [selected, setSelected] = useState([]);
    const [bid, setBid] = useState('');
    const [dailyBudget, setDailyBudget] = useState('');
    const [targeting, setTargeting] = useState({ geo_mode: 'all' });

    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const minBid = useMemo(() => {
        const field = pricingModel === 'cpc' ? 'min_cpc' : 'min_cpm';
        const chosen = placements.filter((p) => selected.includes(p.code));
        if (!chosen.length) return 0;
        return Math.max(...chosen.map((p) => Number(p[field] || 0)));
    }, [placements, selected, pricingModel]);

    const run = async () => {
        if (!selected.length) { setError('Выберите хотя бы одну площадку'); return; }
        try {
            setLoading(true);
            setError(null);
            const res = await APIAdsForecast(token, {
                placements: selected,
                pricingModel,
                bid: Number(bid) || minBid,
                dailyBudget: Number(dailyBudget) || 0,
                targeting,
            });
            if (res?.error) { setError(res.error); setResult(null); return; }
            setResult(res);
        } catch (e) {
            setError(adsErrorText(e, 'Не удалось построить прогноз'));
        } finally {
            setLoading(false);
        }
    };

    const confidence = CONFIDENCE[result?.confidence] || CONFIDENCE.low;

    return (
        <Box>
            <Typography level="h4" sx={{ mb: 0.5 }}>Прогноз бюджета</Typography>
            <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
                Оценка результата до запуска кампании — сколько показов и кликов
                можно получить при заданной ставке
            </Typography>

            {error && <Alert color="danger" sx={{ mb: 2 }}>{error}</Alert>}

            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems="flex-start">
                {/* --- ПАРАМЕТРЫ --- */}
                <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                    <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md', mb: 2 }}>
                        <Typography level="title-sm" sx={{ mb: 1.5 }}>Условия</Typography>

                        <Stack spacing={1.5}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2}>
                                <FormControl sx={{ flex: 1 }}>
                                    <FormLabel>Модель оплаты</FormLabel>
                                    <Select
                                        value={pricingModel}
                                        onChange={(_, v) => v && setPricingModel(v)}
                                    >
                                        {Object.entries(PRICING_LABELS).map(([value, label]) => (
                                            <Option key={value} value={value}>{label}</Option>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl sx={{ flex: 1 }}>
                                    <FormLabel>
                                        {pricingModel === 'cpc' ? 'Ставка за клик, ₽' : 'Ставка за 1000, ₽'}
                                    </FormLabel>
                                    <Input
                                        type="number"
                                        value={bid}
                                        onChange={(e) => setBid(e.target.value)}
                                        placeholder={String(minBid || '')}
                                    />
                                    <FormHelperText>
                                        минимум {formatMoney(minBid)} ₽
                                    </FormHelperText>
                                </FormControl>
                                <FormControl sx={{ flex: 1 }}>
                                    <FormLabel>Дневной бюджет, ₽</FormLabel>
                                    <Input
                                        type="number"
                                        value={dailyBudget}
                                        onChange={(e) => setDailyBudget(e.target.value)}
                                        placeholder={String(limits?.min_daily_budget || 300)}
                                    />
                                </FormControl>
                            </Stack>

                            <Box>
                                <FormLabel sx={{ mb: 1 }}>Площадки</FormLabel>
                                <AdPlacementsPicker
                                    pricingModel={pricingModel}
                                    selected={selected}
                                    onChange={setSelected}
                                />
                            </Box>
                        </Stack>
                    </Sheet>

                    <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md', mb: 2 }}>
                        <Typography level="title-sm" sx={{ mb: 1.5 }}>Аудитория</Typography>
                        <AdTargetingEditor
                            value={targeting}
                            onChange={setTargeting}
                            showReach={false}
                        />
                    </Sheet>

                    <Button
                        size="lg"
                        fullWidth
                        startDecorator={<InsightsRoundedIcon />}
                        loading={loading}
                        onClick={run}
                    >
                        Построить прогноз
                    </Button>
                </Box>

                {/* --- РЕЗУЛЬТАТ --- */}
                <Box sx={{ width: { xs: '100%', lg: 400 }, flexShrink: 0 }}>
                    <Sheet
                        variant="soft"
                        sx={{ p: 2, borderRadius: 'md', position: { lg: 'sticky' }, top: 16 }}
                    >
                        <Typography level="title-sm" sx={{ mb: 1.5 }}>Прогноз</Typography>

                        {loading && <LinearProgress sx={{ mb: 2 }} />}

                        {!result ? (
                            <Typography level="body-sm" color="neutral">
                                Задайте условия и нажмите «Построить прогноз»
                            </Typography>
                        ) : (
                            <>
                                {/* Достоверность рядом с числами, а не мелким шрифтом внизу:
                                    уверенная цифра, промахнувшаяся вдвое, стоит дороже
                                    честной пометки «данных мало» */}
                                <Chip
                                    variant="soft"
                                    color={confidence.color}
                                    sx={{ mb: 1.5 }}
                                >
                                    {confidence.label}
                                </Chip>
                                <Typography level="body-xs" color="neutral" sx={{ mb: 2 }}>
                                    {confidence.hint}
                                </Typography>

                                <Stack spacing={1.2} sx={{ mb: 2 }}>
                                    <Box>
                                        <Typography level="body-xs" color="neutral">
                                            Аудитория под таргетингом
                                        </Typography>
                                        <Typography level="h4">
                                            {formatInt(result.reach)} чел.
                                        </Typography>
                                    </Box>
                                </Stack>

                                <Typography level="body-sm" sx={{ fontWeight: 600, mb: 1 }}>
                                    В сутки
                                </Typography>
                                <Stack spacing={0.5} sx={{ mb: 2 }}>
                                    <Row label="Показы" value={formatInt(result.daily?.impressions)} />
                                    <Row label="Клики" value={formatInt(result.daily?.clicks)} />
                                    <Row label="CTR" value={`${Number(result.daily?.ctr || 0).toFixed(2)}%`} />
                                    <Row label="Цена клика" value={`${formatMoney(result.daily?.cpc)} ₽`} />
                                    <Row label="Расход" value={`${formatMoney(result.daily?.spend)} ₽`} />
                                </Stack>

                                <Typography level="body-sm" sx={{ fontWeight: 600, mb: 1 }}>
                                    В месяц
                                </Typography>
                                <Stack spacing={0.5} sx={{ mb: 2 }}>
                                    <Row label="Показы" value={formatInt(result.monthly?.impressions)} />
                                    <Row label="Клики" value={formatInt(result.monthly?.clicks)} />
                                    <Row label="Расход" value={`${formatMoney(result.monthly?.spend)} ₽`} />
                                </Stack>

                                {result.budget_limited && (
                                    <Alert color="warning" size="sm" sx={{ mb: 2 }}>
                                        Прогноз упёрся в дневной бюджет, а не в доступный инвентарь.
                                        Повышение ставки больше показов не даст — нужно увеличить бюджет.
                                    </Alert>
                                )}

                                {!!(result.suggestions || []).length && (
                                    <>
                                        <Typography level="body-sm" sx={{ fontWeight: 600, mb: 1 }}>
                                            Варианты ставки
                                        </Typography>
                                        <Table size="sm" sx={{ mb: 2 }}>
                                            <thead>
                                                <tr>
                                                    <th>Вариант</th>
                                                    <th style={{ textAlign: 'right' }}>Ставка</th>
                                                    <th style={{ textAlign: 'right' }}>Клики/сут</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {result.suggestions.map((s) => (
                                                    <tr key={s.label}>
                                                        <td>{s.label}</td>
                                                        <td style={{ textAlign: 'right' }}>
                                                            <Box
                                                                component="span"
                                                                sx={{ cursor: 'pointer', color: 'primary.600' }}
                                                                onClick={() => setBid(String(s.bid))}
                                                            >
                                                                {formatMoney(s.bid)} ₽
                                                            </Box>
                                                        </td>
                                                        <td style={{ textAlign: 'right' }}>
                                                            {formatInt(s.clicks)}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </>
                                )}

                                <Typography level="body-xs" color="neutral">
                                    {result.disclaimer}
                                </Typography>
                            </>
                        )}
                    </Sheet>
                </Box>
            </Stack>
        </Box>
    );
}


function Row({ label, value }) {
    return (
        <Stack direction="row" justifyContent="space-between">
            <Typography level="body-sm" color="neutral">{label}</Typography>
            <Typography level="body-sm" sx={{ fontWeight: 600 }}>{value}</Typography>
        </Stack>
    );
}